/* istanbul ignore file
TODO: Add test for expiration validation
*/
import {
  type Dispatch,
  type PropsWithChildren,
  type SetStateAction,
  createContext,
  useContext
} from 'react';

import { handleSaveCardOnFileMutation } from 'payments/helpers/handleSaveCardOnFileMutation';
import { type CardFieldStates } from 'payments/types/card';
import type {
  PartialSaveOptions,
  PaymentFormContextInterface,
  SaveCardOnFileMutation
} from 'payments/types/form';
import { useLoadingSpinner } from 'reservations/base/providers/LoadingSpinnerProvider';
import { validateExpiration } from 'reservations/payments/helpers/validateExpiration';
import { PaymentInformationProps } from 'reservations/payments/types';

const CloverPaymentFormContext =
  createContext<PaymentFormContextInterface | null>(null);

interface ProviderProps extends PaymentInformationProps {
  handleCardSubmit: () => Promise<any>;
  setCardFieldStates: Dispatch<SetStateAction<CardFieldStates>>;
}

export const CloverPaymentFormProvider = ({
  handleCardSubmit,
  handleToken,
  setCardFieldStates,
  bookingDate,
  children
}: PropsWithChildren<ProviderProps>) => {
  const { handleHasLoaded } = useLoadingSpinner();

  const processCard = async () => {
    let { card, token, errors } = await handleCardSubmit();

    if (card) {
      const expirationIsValid = validateExpiration(
        card.exp_month,
        card.exp_year,
        new Date(bookingDate)
      );

      if (!expirationIsValid) {
        setCardFieldStates(currentFieldStates => ({
          ...currentFieldStates,
          expirationDate: {
            ...currentFieldStates.expirationDate,
            error:
              'Your card expires before the reservation date. Please choose a different card.'
          }
        }));
        errors = {
          CARD_DATE:
            'Your card expires before the reservation date. Please choose a different card.'
        };
      }
    }
    handleHasLoaded();
    return { card, token, errors };
  };

  const saveCardOnFile = async (
    saveOptions: PartialSaveOptions,
    mutate: SaveCardOnFileMutation
  ) => {
    const { card, token, errors } = await processCard();

    if (errors || !token || !card) {
      return;
    }

    handleSaveCardOnFileMutation(saveOptions, mutate, token, card);
  };

  const retrieveToken = async () => {
    const { card, token, errors } = await processCard();

    if (errors || !token || !card) {
      return;
    }

    handleToken?.(token, card);
  };

  return (
    <CloverPaymentFormContext.Provider
      value={{ retrieveToken, saveCardOnFile }}
    >
      {children}
    </CloverPaymentFormContext.Provider>
  );
};

export const useCloverPaymentForm = () => useContext(CloverPaymentFormContext);
